import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Row,
  Col,
  Card,
  Form,
  Button,
  Table,
  Pagination,
  Badge,
  Spinner,
  Alert,
  Modal,
  InputGroup,
  Accordion
} from 'react-bootstrap';
import { FaSearch, FaCalendarAlt, FaMapMarkerAlt, FaUsers, FaEye, FaFilter, FaTimes, FaChevronDown, FaChevronUp, FaFileAlt, FaUserTie, FaFilePdf, FaFileExcel } from 'react-icons/fa';
import axios from './utils/axiosConfig';
import Lightbox from 'react-image-lightbox';
import 'react-image-lightbox/style.css';
import jsPDF from 'jspdf';
import * as XLSX from 'xlsx';

const PartnerActivities = () => {
  // 動態活動類別配置（從後端獲取）
  const [categoryList, setCategoryList] = useState([]);

  // 狀態管理
  const [activities, setActivities] = useState([]);
  const [stores, setStores] = useState([]);
  const [activityTypes, setActivityTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // 分頁狀態
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 20
  });

  // 篩選條件
  const [filters, setFilters] = useState({
    storeFilter: '',
    activityName: '',
    activityType: '',
    startDate: '',
    endDate: '',
    sortBy: 'a_Sdate',
    sortOrder: 'desc'
  });

  // UI 狀態
  const [showFilters, setShowFilters] = useState(false);
  const [selectedActivity, setSelectedActivity] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);
  const [lightboxImage, setLightboxImage] = useState('');
  const [expandedActivityId, setExpandedActivityId] = useState(null);
  const [checkinList, setCheckinList] = useState([]);
  const [loadingCheckins, setLoadingCheckins] = useState(false);

  // 社務報告相關狀態
  const [showReportModal, setShowReportModal] = useState(false);
  const [reportFilters, setReportFilters] = useState({
    storeId: '',
    startDate: '',
    endDate: ''
  });
  const [reportData, setReportData] = useState(null);
  const [loadingReport, setLoadingReport] = useState(false);

  // 年度講師相關狀態
  const [showLecturerModal, setShowLecturerModal] = useState(false);
  const [lecturerYear, setLecturerYear] = useState(new Date().getFullYear());
  const [lecturerData, setLecturerData] = useState(null);
  const [lecturerDetails, setLecturerDetails] = useState([]);  // 每次演講的詳細記錄
  const [loadingLecturer, setLoadingLecturer] = useState(false);

  // 各社活動時間一覽表相關狀態
  const [showAllActivitiesModal, setShowAllActivitiesModal] = useState(false);
  const [allActivitiesFilters, setAllActivitiesFilters] = useState({
    startDate: '',
    endDate: ''
  });
  const [allActivitiesData, setAllActivitiesData] = useState(null);
  const [loadingAllActivities, setLoadingAllActivities] = useState(false);

  // 響應式檢測
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // 載入初始資料
  useEffect(() => {
    loadInitialData();
  }, []);

  // 當篩選條件改變時重新載入資料
  useEffect(() => {
    loadActivities();
  }, [filters, pagination.currentPage]);

  const loadInitialData = async () => {
    try {
      const [storesRes, typesRes] = await Promise.all([
        axios.get('/partner-stores'),
        axios.get('/activity-types')
      ]);
      
      setStores(storesRes.data.data || []);
      setActivityTypes(typesRes.data.data || []);
    } catch (error) {
      console.error('載入初始資料失敗:', error);
      setError('載入初始資料失敗，請稍後再試');
    }
  };

  const loadActivities = useCallback(async () => {
    setLoading(true);
    setError('');
    
    try {
      const params = {
        page: pagination.currentPage,
        limit: pagination.itemsPerPage,
        ...filters
      };

      // console.log('發送友社活動請求:', params);
      const response = await axios.get('/partner-activities', { params });
      // console.log('友社活動回應:', response.data);
      
      if (response.data.success) {
        setActivities(response.data.data || []);
        setPagination(prev => ({
          ...prev,
          ...response.data.pagination
        }));
      } else {
        setError(response.data.message || '載入活動資料失敗');
      }
    } catch (error) {
      console.error('載入活動失敗:', error);
      setError('載入活動資料失敗，請稍後再試');
    } finally {
      setLoading(false);
    }
  }, [filters, pagination.currentPage, pagination.itemsPerPage]);

  // 處理篩選條件變更
  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
    setPagination(prev => ({ ...prev, currentPage: 1 }));
  };

  // 清除篩選條件
  const clearFilters = () => {
    setFilters({
      storeFilter: '',
      activityName: '',
      activityType: '',
      startDate: '',
      endDate: '',
      sortBy: 'a_Sdate',
      sortOrder: 'desc'
    });
    setPagination(prev => ({ ...prev, currentPage: 1 }));
  };

  // 處理分頁
  const handlePageChange = (page) => {
    setPagination(prev => ({ ...prev, currentPage: page }));
  };

  // 顯示活動詳情
  const showActivityDetail = (activity) => {
    setSelectedActivity(activity);
    setShowDetailModal(true);
  };

  // 載入簽到名單
  const loadCheckinList = async (activityId) => {
    if (expandedActivityId === activityId) {
      // 如果已展開，則收起
      setExpandedActivityId(null);
      setCheckinList([]);
      return;
    }

    setLoadingCheckins(true);
    setExpandedActivityId(activityId);

    try {
      const response = await axios.get(`/partner-activities/${activityId}/checkins`);
      if (response.data.success) {
        setCheckinList(response.data.data || []);
      } else {
        setCheckinList([]);
        setError('載入簽到名單失敗');
      }
    } catch (error) {
      console.error('載入簽到名單失敗:', error);
      setCheckinList([]);
      setError('載入簽到名單失敗，請稍後再試');
    } finally {
      setLoadingCheckins(false);
    }
  };

  // 生成社務報告
  const generateReport = async () => {
    if (!reportFilters.storeId || !reportFilters.startDate || !reportFilters.endDate) {
      setError('請選擇社別和日期區間');
      return;
    }

    setLoadingReport(true);
    setError('');

    try {
      console.log('📊 請求社務報告:', reportFilters);
      const response = await axios.get('/partner-activities/report', {
        params: reportFilters
      });

      console.log('📥 收到報告資料:', response.data);

      if (response.data.success) {
        setReportData(response.data.data);
        
        // 從後端獲取動態的活動類別清單
        if (response.data.categoryList && response.data.categoryList.length > 0) {
          console.log('📋 收到活動類別清單:', response.data.categoryList);
          setCategoryList(response.data.categoryList);
        }
        
        console.log('✅ 報告資料已設定:', response.data.data);
        
        // 輸出每個類別的資料筆數和第一筆資料
        Object.keys(response.data.data).forEach(key => {
          const items = response.data.data[key];
          console.log(`  ${key}: ${items.length} 筆`);
          if (items.length > 0) {
            console.log(`    第一筆:`, items[0]);
          }
        });
      } else {
        setError(response.data.message || '生成報告失敗');
      }
    } catch (error) {
      console.error('❌ 生成報告失敗:', error);
      setError('生成報告失敗，請稍後再試');
    } finally {
      setLoadingReport(false);
    }
  };

  // 載入年度講師統計
  const loadYearlyLecturers = async () => {
    if (!lecturerYear) {
      setError('請選擇年度');
      return;
    }

    setLoadingLecturer(true);
    setError('');

    try {
      const response = await axios.get('/partner-activities/yearly-lecturers', {
        params: { year: lecturerYear }
      });

      if (response.data.success) {
        setLecturerData(response.data.data);
        setLecturerDetails(response.data.details || []);  // 保存詳細記錄
      } else {
        setError(response.data.message || '載入講師資料失敗');
      }
    } catch (error) {
      console.error('載入講師資料失敗:', error);
      setError('載入講師資料失敗，請稍後再試');
    } finally {
      setLoadingLecturer(false);
    }
  };

  // 載入各社活動時間一覽表
  const loadAllActivities = async () => {
    if (!allActivitiesFilters.startDate || !allActivitiesFilters.endDate) {
      setError('請選擇開始日期和結束日期');
      return;
    }

    setLoadingAllActivities(true);
    setError('');

    try {
      console.log('📊 請求各社活動時間一覽表:', allActivitiesFilters);
      const response = await axios.get('/partner-activities/all-activities', {
        params: allActivitiesFilters
      });

      console.log('📊 各社活動時間一覽表回應:', response.data);

      if (response.data.success) {
        setAllActivitiesData(response.data.data);
      } else {
        setError(response.data.message || '載入活動資料失敗');
      }
    } catch (error) {
      console.error('載入活動資料失敗:', error);
      setError('載入活動資料失敗，請稍後再試');
    } finally {
      setLoadingAllActivities(false);
    }
  };

  // 預覽社務報告 PDF
  const previewReportPDF = async () => {
    console.log('🚀 開始生成 PDF...');

    if (!reportData) {
      console.error('❌ reportData 為空');
      alert('報告資料為空，請先生成報告');
      return;
    }

    console.log('🔍 reportData 內容:', reportData);

    try {
      console.log('📄 初始化 jsPDF...');
      const doc = new jsPDF();

      // 載入中文字體
      console.log('📦 開始載入字體...');
      let fontLoaded = false;
      
      const fontUrls = [
        '/fonts/NotoSansTC-Full.ttf',
        '/fonts/SourceHanSansTW-Regular.ttf'
      ];
      
      for (const fontUrl of fontUrls) {
        try {
          console.log(`  嘗試載入: ${fontUrl}`);
          const response = await fetch(fontUrl);
          
          if (response.ok) {
            console.log('  ✅ 字體檔案下載成功');
            const fontArrayBuffer = await response.arrayBuffer();
            const uint8Array = new Uint8Array(fontArrayBuffer);
            let fontBase64 = '';
            const chunkSize = 8192;
            for (let i = 0; i < uint8Array.length; i += chunkSize) {
              const chunk = uint8Array.subarray(i, i + chunkSize);
              fontBase64 += String.fromCharCode.apply(null, chunk);
            }
            fontBase64 = btoa(fontBase64);
            doc.addFileToVFS("ChineseFont.ttf", fontBase64);
            doc.addFont("ChineseFont.ttf", "ChineseFont", "normal");
            doc.setFont("ChineseFont", "normal");
            console.log('✅ 字體設定完成');
            fontLoaded = true;
            break;
          } else {
            console.warn(`  ⚠️ 字體載入失敗: ${response.status}`);
          }
        } catch (fontError) {
          console.warn(`  ⚠️ 字體載入錯誤: ${fontError.message}`);
        }
      }
      
      if (!fontLoaded) {
        console.error('❌ 所有字體來源都載入失敗，PDF 中文將無法正確顯示');
        alert('字體載入失敗，PDF 中文可能無法完整顯示。請檢查網路連線或聯絡管理員。');
      }

      // 確保字體已設定
      doc.setFont("ChineseFont", "normal");

      const storeName = stores.find(s => s.ss_id === parseInt(reportFilters.storeId))?.ss_name || '未知社別';
      const pageW = doc.internal.pageSize.getWidth();
      let currentY = 20;

      console.log('📄 PDF 生成資訊:', {
        storeName,
        storeId: reportFilters.storeId,
        startDate: reportFilters.startDate,
        endDate: reportFilters.endDate,
        storesCount: stores.length
      });

      // 日期區間放右上角 - 字體 12 * 0.8 = 9.6
      doc.setFontSize(9.6);
      const dateRange = `期間：${reportFilters.startDate}~${reportFilters.endDate}`;
      console.log('📅 日期區間:', dateRange);
      doc.text(dateRange, pageW - 15, currentY, { align: 'right' });

      // 標題居中 - 字體 20 * 0.8 = 16
      doc.setFontSize(16);
      const title = `${storeName}社務報告`;
      console.log('📝 標題文字:', title);
      doc.text(title, pageW / 2, currentY + 5, { align: 'center' });

      // 內容從標題下方開始
      currentY = currentY + 18;

      // 使用動態的類別配置（從後端獲取）
      categoryList.forEach(cat => {
        const key = String(cat.id);  // 用 am_id 作為 key
        const items = reportData[key] || [];
        console.log(`📋 ${cat.name} 資料:`, items.length, '筆');

        if (items.length > 0) {
          doc.setFontSize(14);
          doc.text(cat.name, 20, currentY);
          currentY += 7;

          doc.setFontSize(12);  // 活動內容改為 12pt
          
          // 次團活動需要合併相同名稱的活動
          let processedItems = items;
          if (key === '11') {  // 次團活動 am_id = 11
            console.log('🔄 開始處理次團活動合併...');
            // 將相同活動名稱的記錄合併，收集所有日期
            const groupedActivities = {};
            items.forEach(item => {
              const name = String(item.a_name || '').trim();
              if (name) {
                if (!groupedActivities[name]) {
                  groupedActivities[name] = {
                    ...item,
                    dates: []
                  };
                }
                const date = String(item.a_Sdate || '').trim();
                if (date) {
                  groupedActivities[name].dates.push(date);
                }
              }
            });
            
            console.log('📊 合併後的次團活動:', groupedActivities);
            
            // 轉換為陣列並排序日期
            processedItems = Object.values(groupedActivities).map(group => {
              // 將日期轉換為 Date 物件後排序，再轉回字串
              const sortedDates = group.dates
                .map(d => ({ original: d, date: new Date(d) }))
                .sort((a, b) => a.date - b.date)
                .map(d => d.original);
              
              return {
                ...group,
                dates: sortedDates,
                a_Sdate: sortedDates[0] || '' // 保留第一個日期用於排序
              };
            });
            
            console.log('✅ 次團活動處理完成，共', processedItems.length, '筆');
          }
          
          processedItems.forEach((item, index) => {
            if (currentY > 270) {
              doc.addPage();
              currentY = 20;
            }

            // Debug: 輸出原始資料
            console.log(`🔍 處理 ${cat.name} (ID: ${cat.id}) 第 ${index + 1} 筆:`, {
              a_name: item.a_name,
              a_Sdate: item.a_Sdate,
              lecturer_topics: item.lecturer_topics,
              company_names: item.company_names
            });

            // 清理並組合文字內容
            const date = String(item.a_Sdate || '').trim();
            const name = String(item.a_name || '').trim();
            const lecturers = String(item.lecturer_topics || '').trim();
            const companies = String(item.company_names || '').trim();

            console.log(`  處理後: date="${date}", name="${name}", lecturers="${lecturers}", companies="${companies}"`);

            // 檢查換頁
            if (currentY > 270) {
              doc.addPage();
              currentY = 20;
            }

            // 核心解決方案：將所有半形空格替換為全形空格
            // 因為 jsPDF + 中文字體會吃掉半形空格，但保留全形空格
            const replaceSpaces = (text) => {
              return text ? text.replace(/ /g, '　') : text; // 半形空格 → 全形空格
            };

            // 使用固定位置繪製，確保日期和內容分開
            const dateX = 25;        // 日期起始位置
            const contentX = 55;     // 內容起始位置（日期後30單位）

            // 次團活動特殊處理：顯示活動名稱和所有日期
            if (cat.name === '次團活動' && item.dates && item.dates.length > 0) {
              // 顯示活動名稱（超過5個字自動換行）
              let nameLines = [];
              if (name) {
                if (name.length <= 5) {
                  // 短名稱直接顯示
                  doc.text(replaceSpaces(name), dateX, currentY);
                  nameLines = [name];
                } else {
                  // 長名稱使用 splitTextToSize 換行
                  nameLines = doc.splitTextToSize(replaceSpaces(name), 28);
                  nameLines.forEach((line, lineIndex) => {
                    doc.text(line, dateX, currentY + (lineIndex * 5.5));
                  });
                }
              }
              
              // 在內容位置顯示所有日期（已在合併時排序，直接使用）
              // 每5個日期換一行
              let dateLines = [];
              for (let i = 0; i < item.dates.length; i += 5) {
                const chunk = item.dates.slice(i, i + 5);
                dateLines.push(chunk.join('、'));
              }
              
              // 顯示日期（可能多行）
              dateLines.forEach((line, lineIndex) => {
                doc.text(replaceSpaces(line), contentX, currentY + (lineIndex * 5.5));
              });
              
              // 計算這一筆活動的總高度（取名稱和日期的最大行數）
              const totalLines = Math.max(nameLines.length, dateLines.length);
              currentY += totalLines * 5.5;
            } else {
              // 其他活動類別：日期和內容都自動換行
              
              // 處理日期（自動換行，寬度限制 28）
              let dateLines = [];
              if (date) {
                dateLines = doc.splitTextToSize(replaceSpaces(date), 28);
                dateLines.forEach((line, lineIndex) => {
                  doc.text(line, dateX, currentY + (lineIndex * 5.5));
                });
              }

              // 組合活動名稱 + 講師主題/參訪公司
              let contentLine = '';
              if (name) contentLine += name;

              // 月例會顯示講師主題（直接連接）
              console.log(`  🔍 檢查月例會: cat.name="${cat.name}", cat.id="${cat.id}", lecturers="${lecturers}"`);
              if (cat.name === '月例會' && lecturers !== '' && lecturers !== 'null' && lecturers !== '無' && lecturers !== 'undefined') {
                console.log(`  ✅ 添加講師主題: ${lecturers}`);
                contentLine += '講師主題：' + lecturers;
              }

              // 工商參訪顯示參訪公司（直接連接）
              console.log(`  🔍 檢查工商參訪: cat.name="${cat.name}", cat.id="${cat.id}", companies="${companies}"`);
              if (cat.name === '工商參訪' && companies !== '' && companies !== 'null' && companies !== '無' && companies !== 'undefined') {
                console.log(`  ✅ 添加參訪公司: ${companies}`);
                contentLine += '參訪公司：' + companies;
              }

              // 處理內容（自動換行，寬度限制 130，從 contentX 開始到右邊界）
              console.log(`  📝 最終 contentLine: "${contentLine}"`);
              let contentLines = [];
              if (contentLine) {
                const displayContent = replaceSpaces(contentLine);
                contentLines = doc.splitTextToSize(displayContent, 130);
                console.log(`  📄 分割後行數: ${contentLines.length}`);
                contentLines.forEach((line, lineIndex) => {
                  doc.text(line, contentX, currentY + (lineIndex * 5.5));
                });
              }

              // 計算這一筆活動的總高度（取日期和內容的最大行數）
              const totalLines = Math.max(dateLines.length, contentLines.length);
              currentY += Math.max(totalLines, 1) * 5.5; // 至少一行的高度
            }

            console.log(`  ✅ 第 ${index + 1} 筆處理完成`);
          });

          currentY += 5;
        }
      });

      // 生成檔案名稱（使用前面已宣告的 storeName）
      const fileName = `${storeName}_社務報告_${reportFilters.startDate}_${reportFilters.endDate}.pdf`;

      // 生成 PDF Blob 並建立帶檔名的下載連結
      const pdfBlob = doc.output('blob');
      const pdfUrl = URL.createObjectURL(pdfBlob);

      // 建立 iframe 來預覽 PDF（避免自動下載）
      const iframe = document.createElement('iframe');
      iframe.style.display = 'none';
      iframe.src = pdfUrl;
      document.body.appendChild(iframe);

      // 在新視窗開啟（支援瀏覽器預覽）
      const newWindow = window.open('', '_blank');
      if (newWindow) {
        newWindow.document.write(`
          <!DOCTYPE html>
          <html>
          <head>
            <title>${fileName}</title>
            <style>
              body { margin: 0; padding: 0; overflow: hidden; }
              iframe { width: 100vw; height: 100vh; border: none; }
              .download-btn {
                position: fixed;
                top: 10px;
                right: 10px;
                z-index: 9999;
                padding: 10px 20px;
                background: #1890ff;
                color: white;
                border: none;
                border-radius: 4px;
                cursor: pointer;
                font-size: 14px;
                box-shadow: 0 2px 8px rgba(0,0,0,0.15);
              }
              .download-btn:hover {
                background: #40a9ff;
              }
            </style>
          </head>
          <body>
            <button class="download-btn" onclick="downloadPDF()">📥 下載 PDF</button>
            <iframe src="${pdfUrl}"></iframe>
            <script>
              function downloadPDF() {
                const link = document.createElement('a');
                link.href = '${pdfUrl}';
                link.download = '${fileName}';
                link.click();
              }
            </script>
          </body>
          </html>
        `);
      }

      // 延長 URL 清理時間
      setTimeout(() => {
        URL.revokeObjectURL(pdfUrl);
        if (iframe && iframe.parentNode) {
          iframe.parentNode.removeChild(iframe);
        }
      }, 60000); // 60秒後清理

      console.log('✅ PDF 生成並開啟成功！');
    } catch (error) {
      console.error('❌ 預覽PDF失敗:', error);
      console.error('❌ 錯誤詳情:', error.message);
      console.error('❌ 錯誤堆疊:', error.stack);
      alert('預覽PDF失敗: ' + error.message);
      setError('預覽PDF失敗，請稍後再試');
    }
  };

  // 預覽年度講師 PDF
  const previewLecturerPDF = async () => {
    console.log('🚀 開始生成年度講師 PDF...');

    if (!lecturerData || lecturerData.length === 0) {
      console.error('❌ lecturerData 為空');
      alert('講師資料為空，請先查詢年度講師');
      return;
    }

    console.log('🔍 lecturerData 內容:', lecturerData);

    try {
      console.log('📄 初始化 jsPDF...');
      const doc = new jsPDF();

      // 載入中文字體
      console.log('📦 開始載入字體...');
      let fontLoaded = false;

      const fontUrls = [
        '/fonts/NotoSansTC-Full.ttf',
        '/fonts/SourceHanSansTW-Regular.ttf'
      ];

      for (const fontUrl of fontUrls) {
        try {
          console.log(`  嘗試載入: ${fontUrl}`);
          const response = await fetch(fontUrl);

          if (response.ok) {
            console.log('  ✅ 字體檔案下載成功');
            const fontArrayBuffer = await response.arrayBuffer();
            const uint8Array = new Uint8Array(fontArrayBuffer);
            let fontBase64 = '';
            const chunkSize = 8192;
            for (let i = 0; i < uint8Array.length; i += chunkSize) {
              const chunk = uint8Array.subarray(i, i + chunkSize);
              fontBase64 += String.fromCharCode.apply(null, chunk);
            }
            fontBase64 = btoa(fontBase64);
            doc.addFileToVFS("ChineseFont.ttf", fontBase64);
            doc.addFont("ChineseFont.ttf", "ChineseFont", "normal");
            doc.setFont("ChineseFont", "normal");
            console.log('✅ 字體設定完成');
            fontLoaded = true;
            break;
          } else {
            console.warn(`  ⚠️ 字體載入失敗: ${response.status}`);
          }
        } catch (fontError) {
          console.warn(`  ⚠️ 字體載入錯誤: ${fontError.message}`);
        }
      }

      if (!fontLoaded) {
        console.error('❌ 所有字體來源都載入失敗，PDF 中文將無法正確顯示');
        alert('字體載入失敗，PDF 中文可能無法完整顯示。請檢查網路連線或聯絡管理員。');
      }

      // 確保字體已設定
      doc.setFont("ChineseFont", "normal");

      const pageW = doc.internal.pageSize.getWidth();
      let currentY = 20;

      // 日期區間放右上角
      doc.setFontSize(9.6);
      const currentDate = new Date().toLocaleDateString('zh-TW');
      doc.text(`列印日期：${currentDate}`, pageW - 15, currentY, { align: 'right' });

      // 標題居中
      doc.setFontSize(16);
      const title = `${lecturerYear}年度講師統計`;
      console.log('📝 標題文字:', title);
      doc.text(title, pageW / 2, currentY + 5, { align: 'center' });

      // 講師數量與演講次數
      doc.setFontSize(10);
      const totalLectures = lecturerDetails.length;
      doc.text(`共　${lecturerData.length}　位講師，${totalLectures}　場演講`, pageW / 2, currentY + 12, { align: 'center' });

      currentY = currentY + 25;

      // 表格標題
      doc.setFontSize(10);
      const headerY = currentY;
      const colX = {
        no: 12,
        name: 22,
        count: 52,
        date: 62,
        topic: 92,
        store: 182
      };

      doc.text('#', colX.no, headerY);
      doc.text('講師姓名', colX.name, headerY);
      doc.text('次數', colX.count, headerY);
      doc.text('活動日期', colX.date, headerY);
      doc.text('演講主題', colX.topic, headerY);
      doc.text('社別', colX.store, headerY);

      currentY += 3;

      // 分隔線（在標題下方）
      doc.setLineWidth(0.5);
      doc.line(10, currentY, pageW - 10, currentY);

      currentY += 5; // 分隔線與第一筆資料的間距

      // 表格內容
      doc.setFontSize(11);

      const replaceSpaces = (text) => {
        return text ? text.replace(/ /g, '　') : text;
      };

      // 將詳細資料按講師分組
      const lecturerMap = new Map();
      lecturerDetails.forEach(detail => {
        if (!lecturerMap.has(detail.l_id)) {
          lecturerMap.set(detail.l_id, []);
        }
        lecturerMap.get(detail.l_id).push(detail);
      });

      let rowIndex = 0;
      
      // 按照 lecturerData 的順序（演講次數由多到少）來顯示
      lecturerData.forEach((lecturer) => {
        const details = lecturerMap.get(lecturer.l_id) || [];
        
        if (details.length === 0) return;

        details.forEach((detail, detailIndex) => {
          // 檢查是否需要換頁
          if (currentY > 275) {
            doc.addPage();
            doc.setFont("ChineseFont", "normal");
            currentY = 20;
          }

          rowIndex++;
          
          const name = replaceSpaces(String(detail.lecturer_name || ''));
          const count = String(lecturer.topic_count || 0);
          const date = replaceSpaces(String(detail.activity_date || ''));
          const topic = replaceSpaces(String(detail.topic_name || '-'));
          const store = replaceSpaces(String(detail.store_name || '-'));

          // 序號
          doc.text(String(rowIndex), colX.no, currentY);

          // 講師姓名和次數只在該講師第一筆時顯示
          let nameLines = [];
          if (detailIndex === 0) {
            // 短名字直接顯示，長名字才換行
            if (name.length <= 5) {
              doc.text(name, colX.name, currentY);
              nameLines = [name];
            } else {
              nameLines = doc.splitTextToSize(name, 30);
              nameLines.forEach((line, lineIndex) => {
                doc.text(line, colX.name, currentY + (lineIndex * 4));
              });
            }
            doc.text(count, colX.count, currentY);
          }

          // 活動日期
          doc.text(date, colX.date, currentY);

          // 演講主題（可能需要換行）
          const topicLines = doc.splitTextToSize(topic, 85);
          topicLines.forEach((line, lineIndex) => {
            doc.text(line, colX.topic, currentY + (lineIndex * 4));
          });

          // 社別
          doc.text(store, colX.store, currentY);

          // 計算這一行的高度（考慮講師姓名、主題的換行）
          const rowHeight = Math.max(nameLines.length * 4, topicLines.length * 4, 5);
          currentY += rowHeight + 1;
        });

        // 每位講師之間加一條分隔線
        if (currentY < 270) {
          doc.setDrawColor(200, 200, 200);
          doc.setLineWidth(0.2);
          doc.line(10, currentY, pageW - 10, currentY);
          doc.setDrawColor(0, 0, 0);
          currentY += 3;
        }
      });

      // 生成檔案名稱
      const fileName = `${lecturerYear}年度講師統計.pdf`;

      // 生成 PDF Blob 並建立帶檔名的下載連結
      const pdfBlob = doc.output('blob');
      const pdfUrl = URL.createObjectURL(pdfBlob);

      // 建立 iframe 來預覽 PDF（避免自動下載）
      const iframe = document.createElement('iframe');
      iframe.style.display = 'none';
      iframe.src = pdfUrl;
      document.body.appendChild(iframe);

      // 在新視窗開啟（支援瀏覽器預覽）
      const newWindow = window.open('', '_blank');
      if (newWindow) {
        newWindow.document.write(`
          <!DOCTYPE html>
          <html>
          <head>
            <title>${fileName}</title>
            <style>
              body { margin: 0; padding: 0; overflow: hidden; }
              iframe { width: 100vw; height: 100vh; border: none; }
              .download-btn {
                position: fixed;
                top: 10px;
                right: 10px;
                z-index: 9999;
                padding: 10px 20px;
                background: #1890ff;
                color: white;
                border: none;
                border-radius: 4px;
                cursor: pointer;
                font-size: 14px;
                box-shadow: 0 2px 8px rgba(0,0,0,0.15);
              }
              .download-btn:hover {
                background: #40a9ff;
              }
            </style>
          </head>
          <body>
            <button class="download-btn" onclick="downloadPDF()">📥 下載 PDF</button>
            <iframe src="${pdfUrl}"></iframe>
            <script>
              function downloadPDF() {
                const link = document.createElement('a');
                link.href = '${pdfUrl}';
                link.download = '${fileName}';
                link.click();
              }
            </script>
          </body>
          </html>
        `);
      }

      // 延長 URL 清理時間
      setTimeout(() => {
        URL.revokeObjectURL(pdfUrl);
        if (iframe && iframe.parentNode) {
          iframe.parentNode.removeChild(iframe);
        }
      }, 60000); // 60秒後清理

      console.log('✅ 年度講師 PDF 生成並開啟成功！');
    } catch (error) {
      console.error('❌ 預覽年度講師PDF失敗:', error);
      console.error('❌ 錯誤詳情:', error.message);
      alert('預覽PDF失敗: ' + error.message);
      setError('預覽PDF失敗，請稍後再試');
    }
  };

  // 預覽各社活動時間一覽表 PDF
  const previewAllActivitiesPDF = async () => {
    console.log('🚀 開始生成各社活動時間一覽表 PDF...');

    if (!allActivitiesData || allActivitiesData.length === 0) {
      console.error('❌ allActivitiesData 為空');
      alert('活動資料為空，請先查詢活動');
      return;
    }

    console.log('🔍 allActivitiesData 內容:', allActivitiesData);

    try {
      console.log('📄 初始化 jsPDF...');
      const doc = new jsPDF();

      // 載入中文字體
      console.log('📦 開始載入字體...');
      let fontLoaded = false;

      const fontUrls = [
        '/fonts/NotoSansTC-Full.ttf',
        '/fonts/SourceHanSansTW-Regular.ttf'
      ];

      for (const fontUrl of fontUrls) {
        try {
          console.log(`  嘗試載入: ${fontUrl}`);
          const response = await fetch(fontUrl);

          if (response.ok) {
            console.log('  ✅ 字體檔案下載成功');
            const fontArrayBuffer = await response.arrayBuffer();
            const uint8Array = new Uint8Array(fontArrayBuffer);
            let fontBase64 = '';
            const chunkSize = 8192;
            for (let i = 0; i < uint8Array.length; i += chunkSize) {
              const chunk = uint8Array.subarray(i, i + chunkSize);
              fontBase64 += String.fromCharCode.apply(null, chunk);
            }
            fontBase64 = btoa(fontBase64);
            doc.addFileToVFS("ChineseFont.ttf", fontBase64);
            doc.addFont("ChineseFont.ttf", "ChineseFont", "normal");
            doc.setFont("ChineseFont", "normal");
            console.log('✅ 字體設定完成');
            fontLoaded = true;
            break;
          } else {
            console.warn(`  ⚠️ 字體載入失敗: ${response.status}`);
          }
        } catch (fontError) {
          console.warn(`  ⚠️ 字體載入錯誤: ${fontError.message}`);
        }
      }

      if (!fontLoaded) {
        console.error('❌ 所有字體來源都載入失敗，PDF 中文將無法正確顯示');
        alert('字體載入失敗，PDF 中文可能無法完整顯示。請檢查網路連線或聯絡管理員。');
      }

      // 確保字體已設定
      doc.setFont("ChineseFont", "normal");

      const pageW = doc.internal.pageSize.getWidth();
      const pageH = doc.internal.pageSize.getHeight();
      let currentY = 20;

      // 日期區間放右上角
      doc.setFontSize(9.6);
      doc.text(`列印日期：${new Date().toLocaleDateString('zh-TW')}`, pageW - 15, currentY, { align: 'right' });

      // 標題居中
      doc.setFontSize(16);
      const title = `各社活動時間一覽表`;
      doc.text(title, pageW / 2, currentY + 5, { align: 'center' });

      // 期間說明 - 使用全形的「一」分隔日期
      doc.setFontSize(10);
      const periodText = `期間：${allActivitiesFilters.startDate}一${allActivitiesFilters.endDate}`;
      doc.text(periodText, pageW / 2, currentY + 12, { align: 'center' });

      // 活動數量
      doc.text(`共　${allActivitiesData.length}　個活動`, pageW / 2, currentY + 17, { align: 'center' });

      currentY = currentY + 28;

      // 表格標題
      doc.setFontSize(10);
      const headerY = currentY;
      const colX = {
        no: 15,
        date: 25,
        category: 50,
        store: 75,
        name: 95,
        location: 155
      };

      doc.text('#', colX.no, headerY);
      doc.text('活動日期', colX.date, headerY);
      doc.text('類別', colX.category, headerY);
      doc.text('社別', colX.store, headerY);
      doc.text('活動名稱', colX.name, headerY);
      doc.text('地址', colX.location, headerY);

      currentY += 3;

      // 分隔線
      doc.setLineWidth(0.5);
      doc.line(10, currentY, pageW - 10, currentY);

      currentY += 7;

      // 空格替換函數
      const replaceSpaces = (text) => {
        return text ? text.replace(/ /g, '　') : text;
      };

      // 表格內容
      doc.setFontSize(9);
      
      // Debug: 檢查前5筆資料
      console.log('📊 PDF 前5筆資料:', allActivitiesData.slice(0, 5).map(a => ({
        a_name: a.a_name,
        a_Sdate: a.a_Sdate,
        a_Edate: a.a_Edate
      })));

      allActivitiesData.forEach((activity, index) => {
        // 檢查是否需要換頁
        if (currentY > pageH - 20) {
          doc.addPage();
          doc.setFont("ChineseFont", "normal");
          currentY = 20;
        }

        // 組合日期：如果開始日期和結束日期不同，顯示為 "開始~結束"
        let dateDisplay = activity.a_Sdate || '';
        if (activity.a_Edate && activity.a_Edate !== activity.a_Sdate) {
          dateDisplay = `${activity.a_Sdate}~${activity.a_Edate}`;
          console.log(`📅 跨日活動: ${activity.a_name}, ${dateDisplay}`);
        }

        // 所有欄位都需要經過 replaceSpaces 處理
        const date = replaceSpaces(String(dateDisplay));
        const category = replaceSpaces(String(activity.activity_type_name || '-'));
        const store = replaceSpaces(String(activity.store_name || '-'));
        const name = replaceSpaces(String(activity.a_name || ''));
        const location = replaceSpaces(String(activity.a_location || '-'));

        // 計算是否需要顯示日期和類別（與前端邏輯一致）
        const showDate = index === 0 ||
          allActivitiesData[index - 1].a_Sdate !== activity.a_Sdate;
        const showCategory = index === 0 ||
          allActivitiesData[index - 1].a_Sdate !== activity.a_Sdate ||
          allActivitiesData[index - 1].activity_type_name !== activity.activity_type_name;

        // 序號
        doc.text(String(index + 1), colX.no, currentY);

        // 日期（只在需要時顯示）
        if (showDate) {
          doc.text(date, colX.date, currentY);
        }

        // 類別（只在需要時顯示）
        if (showCategory) {
          doc.text(category, colX.category, currentY);
        }

        // 社別
        doc.text(store, colX.store, currentY);

        // 活動名稱（可能需要換行）
        const nameLines = doc.splitTextToSize(name, 55);
        nameLines.forEach((line, lineIndex) => {
          doc.text(line, colX.name, currentY + (lineIndex * 4));
        });

        // 地址（可能需要換行）
        const locationLines = doc.splitTextToSize(location, 35);
        locationLines.forEach((line, lineIndex) => {
          doc.text(line, colX.location, currentY + (lineIndex * 4));
        });

        // 計算這一行的高度
        const maxLines = Math.max(nameLines.length, locationLines.length, 1);
        currentY += maxLines * 4 + 2;
      });

      // 生成檔案名稱
      const fileName = `各社活動時間一覽表_${allActivitiesFilters.startDate}_${allActivitiesFilters.endDate}.pdf`;

      // 生成 PDF Blob
      const pdfBlob = doc.output('blob');
      const pdfUrl = URL.createObjectURL(pdfBlob);

      // 偵測是否為手機裝置
      const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

      if (isMobile) {
        // 手機裝置：直接下載 PDF
        const link = document.createElement('a');
        link.href = pdfUrl;
        link.download = fileName;
        link.click();
        
        // 清理 URL
        setTimeout(() => {
          URL.revokeObjectURL(pdfUrl);
        }, 1000);
      } else {
        // 桌面裝置：在新視窗預覽
        const newWindow = window.open('', '_blank');
        if (newWindow) {
          newWindow.document.write(`
            <!DOCTYPE html>
            <html>
            <head>
              <title>${fileName}</title>
              <style>
                body { margin: 0; padding: 0; overflow: hidden; }
                iframe { width: 100vw; height: 100vh; border: none; }
                .download-btn {
                  position: fixed;
                  top: 10px;
                  right: 10px;
                  z-index: 9999;
                  padding: 10px 20px;
                  background: #1890ff;
                  color: white;
                  border: none;
                  border-radius: 4px;
                  cursor: pointer;
                  font-size: 14px;
                  box-shadow: 0 2px 8px rgba(0,0,0,0.15);
                }
                .download-btn:hover {
                  background: #40a9ff;
                }
              </style>
            </head>
            <body>
              <button class="download-btn" onclick="downloadPDF()">📥 下載 PDF</button>
              <iframe src="${pdfUrl}"></iframe>
              <script>
                function downloadPDF() {
                  const link = document.createElement('a');
                  link.href = '${pdfUrl}';
                  link.download = '${fileName}';
                  link.click();
                }
              </script>
            </body>
            </html>
          `);
        }

        // 延長 URL 清理時間
        setTimeout(() => {
          URL.revokeObjectURL(pdfUrl);
        }, 60000); // 60秒後清理
      }

      console.log('✅ 各社活動時間一覽表 PDF 生成並開啟成功！');
    } catch (error) {
      console.error('❌ 預覽各社活動時間一覽表PDF失敗:', error);
      console.error('❌ 錯誤詳情:', error.message);
      alert('預覽PDF失敗: ' + error.message);
      setError('預覽PDF失敗，請稍後再試');
    }
  };

  // 匯出 Excel
  const exportReportToExcel = () => {
    if (!reportData) {
      alert('請先生成報告');
      return;
    }

    try {
      console.log('📊 開始生成 Excel...');
      
      const storeName = stores.find(s => s.ss_id === parseInt(reportFilters.storeId))?.ss_name || '';
      const workbook = XLSX.utils.book_new();

      // 使用動態的類別配置
      // 為每個類別建立工作表
      categoryList.forEach(cat => {
        const key = String(cat.id);  // 用 am_id 作為 key
        const items = reportData[key] || [];
        
        if (items.length > 0) {
          // 準備資料
          const data = items.map((item, index) => {
            const row = {
              '序號': index + 1,
              '活動日期': item.a_Sdate,
              '活動名稱': item.a_name || '',
              '活動地點': item.a_location || ''
            };

            // 月例會加入講師主題
            if (key === '1' && item.lecturer_topics && item.lecturer_topics !== 'null') {
              row['講師與主題'] = item.lecturer_topics;
            }

            // 工商參訪加入參訪公司
            if (key === '9' && item.company_names && item.company_names !== 'null') {
              row['參訪公司'] = item.company_names;
            }

            return row;
          });

          // 建立工作表
          const worksheet = XLSX.utils.json_to_sheet(data);
          
          // 設定欄位寬度
          const colWidths = [
            { wch: 8 },  // 序號
            { wch: 12 }, // 活動日期
            { wch: 30 }, // 活動名稱
            { wch: 25 }, // 活動地點
            { wch: 50 }  // 講師主題/參訪公司
          ];
          worksheet['!cols'] = colWidths;

          // 加入工作表到工作簿
          XLSX.utils.book_append_sheet(workbook, worksheet, cat.name);
          console.log(`  ✅ ${cat.name} 工作表建立完成 (${items.length} 筆)`);
        }
      });

      // 產生檔案名稱
      const fileName = `${storeName}_社務報告_${reportFilters.startDate}_${reportFilters.endDate}.xlsx`;
      
      // 下載檔案
      XLSX.writeFile(workbook, fileName);
      console.log('✅ Excel 檔案下載完成:', fileName);
      
    } catch (error) {
      console.error('❌ 匯出 Excel 失敗:', error);
      alert('匯出 Excel 失敗: ' + error.message);
      setError('匯出 Excel 失敗，請稍後再試');
    }
  };

  // 渲染篩選區域
  const renderFilters = () => (
    <Card className="mb-3">
      <Card.Header className="d-flex justify-content-between align-items-center">
        <h6 className="mb-0">
          <FaFilter className="me-2" />
          篩選條件
        </h6>
        <Button 
          variant="outline-secondary" 
          size="sm"
          onClick={() => setShowFilters(!showFilters)}
        >
          {showFilters ? <FaTimes /> : <FaFilter />}
        </Button>
      </Card.Header>
      
      {(showFilters || !isMobile) && (
        <Card.Body>
          <Row className="g-3">
            <Col xs={12} md={6} lg={3}>
              <Form.Group>
                <Form.Label>社別</Form.Label>
                <Form.Select
                  value={filters.storeFilter}
                  onChange={(e) => handleFilterChange('storeFilter', e.target.value)}
                >
                  <option value="">全部社別</option>
                  {stores.map(store => (
                    <option key={store.ss_id} value={store.ss_id}>
                      {store.ss_name}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            
            <Col xs={12} md={6} lg={3}>
              <Form.Group>
                <Form.Label>活動類別</Form.Label>
                <Form.Select
                  value={filters.activityType}
                  onChange={(e) => handleFilterChange('activityType', e.target.value)}
                >
                  <option value="">全部類別</option>
                  {activityTypes.map(type => (
                    <option key={type.am_id} value={type.am_id}>
                      {type.am_name}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            
            <Col xs={12} md={6} lg={3}>
              <Form.Group>
                <Form.Label>開始日期</Form.Label>
                <Form.Control
                  type="date"
                  value={filters.startDate}
                  onChange={(e) => handleFilterChange('startDate', e.target.value)}
                />
              </Form.Group>
            </Col>
            
            <Col xs={12} md={6} lg={3}>
              <Form.Group>
                <Form.Label>結束日期</Form.Label>
                <Form.Control
                  type="date"
                  value={filters.endDate}
                  onChange={(e) => handleFilterChange('endDate', e.target.value)}
                />
              </Form.Group>
            </Col>
            
            <Col xs={12}>
              <Form.Group>
                <Form.Label>活動名稱</Form.Label>
                <InputGroup>
                  <Form.Control
                    type="text"
                    placeholder="搜尋活動名稱..."
                    value={filters.activityName}
                    onChange={(e) => handleFilterChange('activityName', e.target.value)}
                  />
                  <Button variant="outline-secondary">
                    <FaSearch />
                  </Button>
                </InputGroup>
              </Form.Group>
            </Col>
          </Row>
          
          <div className="d-flex justify-content-between mt-3">
            <Button variant="outline-danger" onClick={clearFilters}>
              清除篩選
            </Button>
            <div>
              <Form.Select
                size="sm"
                value={`${filters.sortBy}-${filters.sortOrder}`}
                onChange={(e) => {
                  const [sortBy, sortOrder] = e.target.value.split('-');
                  handleFilterChange('sortBy', sortBy);
                  handleFilterChange('sortOrder', sortOrder);
                }}
                style={{ width: 'auto', display: 'inline-block' }}
              >
                <option value="a_Sdate-desc">日期 (新到舊)</option>
                <option value="a_Sdate-asc">日期 (舊到新)</option>
                <option value="a_name-asc">名稱 (A-Z)</option>
                <option value="a_name-desc">名稱 (Z-A)</option>
              </Form.Select>
            </div>
          </div>
        </Card.Body>
      )}
    </Card>
  );

  // 渲染活動卡片 (手機版)
  const renderActivityCards = () => (
    <Row>
      {activities.map(activity => (
        <Col xs={12} sm={6} lg={4} key={activity.a_id} className="mb-3">
          <Card className="h-100 shadow-sm">
            {activity.a_EDM && (
              <div style={{ position: 'relative', height: '200px', overflow: 'hidden' }}>
                <Card.Img
                  variant="top"
                  src={`/uploads/AEDM/${activity.a_EDM}`}
                  style={{ 
                    height: '100%', 
                    objectFit: 'cover',
                    cursor: 'pointer'
                  }}
                  onClick={() => {
                    setLightboxImage(`/uploads/AEDM/${activity.a_EDM}`);
                    setIsLightboxOpen(true);
                  }}
                />
              </div>
            )}
            
            <Card.Body className="d-flex flex-column">
              <div className="mb-2">
                <Badge bg="primary" className="me-2">{activity.store_name}</Badge>
                {activity.activity_type_name && (
                  <Badge bg="secondary">{activity.activity_type_name}</Badge>
                )}
              </div>
              
              <div className="mb-2">
                <small className="text-muted">
                  <FaCalendarAlt className="me-1" />
                  {activity.a_Sdate}
                  {activity.a_Edate && activity.a_Edate !== activity.a_Sdate && 
                    ` ~ ${activity.a_Edate}`
                  }
                </small>
              </div>
              
              <Card.Title className="h6">{activity.a_name}</Card.Title>
              
              <div className="mb-2">
                <small className="text-muted">
                  <FaUsers className="me-1" />
                  報名: {activity.registered_count} | 簽到: {activity.checked_in_count} | 請假: {activity.leave_count}
                </small>
              </div>
              
              {activity.company_names && (
                <div className="mb-2">
                  <small className="text-muted">
                    <strong>參訪公司:</strong><br/>
                    {activity.company_names}
                  </small>
                </div>
              )}
              
              {activity.lecturer_topics && (
                <div className="mb-2">
                  <small className="text-muted">
                    <strong>講師與主題:</strong><br/>
                    {activity.lecturer_topics}
                  </small>
                </div>
              )}
              
              {activity.a_location && (
                <div className="mb-2">
                  <small className="text-muted">
                    <FaMapMarkerAlt className="me-1" />
                    <a
                      href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(activity.a_location)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: '#17a2b8' }}
                    >
                      {activity.a_location}
                    </a>
                  </small>
                </div>
              )}
              
              <div className="mt-auto">
                <div className="d-grid gap-2">
                  <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={() => showActivityDetail(activity)}
                  >
                    <FaEye className="me-1" />
                    查看詳情
                  </Button>
                  {activity.checked_in_count > 0 && (
                    <Button
                      variant="outline-info"
                      size="sm"
                      onClick={() => loadCheckinList(activity.a_id)}
                    >
                      {expandedActivityId === activity.a_id ? (
                        <><FaChevronUp className="me-1" />收起簽到名單</>
                      ) : (
                        <><FaChevronDown className="me-1" />簽到名單 ({activity.checked_in_count})</>
                      )}
                    </Button>
                  )}
                </div>
              </div>
            </Card.Body>

            {expandedActivityId === activity.a_id && (
              <Card.Footer className="bg-light">
                {loadingCheckins ? (
                  <div className="text-center py-2">
                    <Spinner animation="border" size="sm" />
                    <span className="ms-2">載入中...</span>
                  </div>
                ) : checkinList.length === 0 ? (
                  <div className="text-center py-2 text-muted">
                    尚無簽到紀錄
                  </div>
                ) : (
                  <div>
                    <h6 className="mb-2">簽到名單 ({checkinList.length} 人)</h6>
                    <div style={{maxHeight: '300px', overflowY: 'auto'}}>
                      {checkinList.map((checkin, index) => (
                        <Card key={checkin.id} className="mb-2" style={{fontSize: '0.85rem'}}>
                          <Card.Body className="p-2">
                            <div className="d-flex justify-content-between align-items-start">
                              <div>
                                <strong>#{index + 1} {checkin.e_name}</strong>
                                <div className="text-muted">
                                  {checkin.store_name} · {checkin.position_name || '-'}
                                </div>
                                <small className="text-muted">{checkin.checkin_time}</small>
                              </div>
                              <Badge bg="info">
                                {checkin.checkin_method === 'mobile' ? '手機' :
                                 checkin.checkin_method === 'line' ? 'LINE' :
                                 checkin.checkin_method === 'face' ? '人臉' :
                                 checkin.checkin_method === 'admin' ? '管理員' :
                                 checkin.checkin_method === 'excel' ? 'Excel' :
                                 checkin.checkin_method}
                              </Badge>
                            </div>
                          </Card.Body>
                        </Card>
                      ))}
                    </div>
                  </div>
                )}
              </Card.Footer>
            )}
          </Card>
        </Col>
      ))}
    </Row>
  );

  // 渲染活動表格 (桌面版)
  const renderActivityTable = () => (
    <Card>
      <div className="table-responsive">
        <Table striped hover>
          <thead className="table-dark">
            <tr>
              <th style={{width: '40px'}}></th>
              <th>社別</th>
              <th>類別</th>
              <th>活動日期</th>
              <th>活動名稱</th>
              <th>參訪公司</th>
              <th>講師與主題</th>
              <th>地點</th>
              <th>報名狀況</th>
              <th>操作</th>
            </tr>
          </thead>
          <tbody>
            {activities.map(activity => (
              <React.Fragment key={activity.a_id}>
                <tr>
                  <td>
                    <Button
                      variant="link"
                      size="sm"
                      onClick={() => loadCheckinList(activity.a_id)}
                      disabled={activity.checked_in_count === 0}
                      title={activity.checked_in_count === 0 ? '尚無簽到紀錄' : '展開簽到名單'}
                    >
                      {activity.checked_in_count > 0 && (
                        expandedActivityId === activity.a_id ?
                          <FaChevronUp /> : <FaChevronDown />
                      )}
                    </Button>
                  </td>
                  <td>
                    <Badge bg="primary">{activity.store_name}</Badge>
                  </td>
                  <td>
                    {activity.activity_type_name && (
                      <Badge bg="secondary">{activity.activity_type_name}</Badge>
                    )}
                  </td>
                  <td>
                    {activity.a_Sdate}
                    {activity.a_Edate && activity.a_Edate !== activity.a_Sdate &&
                      <><br/><small className="text-muted">~ {activity.a_Edate}</small></>
                    }
                  </td>
                  <td>{activity.a_name}</td>
                  <td>
                    <small>{activity.company_names || '無'}</small>
                  </td>
                  <td>
                    <small>{activity.lecturer_topics || '無'}</small>
                  </td>
                  <td>{activity.a_location || '-'}</td>
                  <td>
                    <small>
                      報名: <span className="text-success">{activity.registered_count}</span><br/>
                      簽到: <span className="text-primary">{activity.checked_in_count}</span><br/>
                      請假: <span className="text-warning">{activity.leave_count}</span>
                    </small>
                  </td>
                  <td>
                    <Button
                      variant="outline-primary"
                      size="sm"
                      onClick={() => showActivityDetail(activity)}
                    >
                      <FaEye />
                    </Button>
                  </td>
                </tr>
                {expandedActivityId === activity.a_id && (
                  <tr>
                    <td colSpan="10" className="bg-light">
                      {loadingCheckins ? (
                        <div className="text-center py-3">
                          <Spinner animation="border" size="sm" />
                          <span className="ms-2">載入簽到名單中...</span>
                        </div>
                      ) : checkinList.length === 0 ? (
                        <div className="text-center py-3 text-muted">
                          尚無簽到紀錄
                        </div>
                      ) : (
                        <div className="p-3">
                          <h6 className="mb-3">簽到名單 ({checkinList.length} 人)</h6>
                          <Table size="sm" bordered className="mb-0">
                            <thead className="table-secondary">
                              <tr>
                                <th style={{width: '60px'}}>#</th>
                                <th>社別</th>
                                <th>社職</th>
                                <th>姓名</th>
                                <th>簽到時間</th>
                                <th>簽到方式</th>
                              </tr>
                            </thead>
                            <tbody>
                              {checkinList.map((checkin, index) => (
                                <tr key={checkin.id}>
                                  <td>{index + 1}</td>
                                  <td>{checkin.store_name}</td>
                                  <td>{checkin.position_name || '-'}</td>
                                  <td>{checkin.e_name}</td>
                                  <td>{checkin.checkin_time}</td>
                                  <td>
                                    <Badge bg="info">
                                      {checkin.checkin_method === 'mobile' ? '手機' :
                                       checkin.checkin_method === 'line' ? 'LINE' :
                                       checkin.checkin_method === 'face' ? '人臉' :
                                       checkin.checkin_method === 'admin' ? '管理員' :
                                       checkin.checkin_method === 'excel' ? 'Excel' :
                                       checkin.checkin_method}
                                    </Badge>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </Table>
                        </div>
                      )}
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </Table>
      </div>
    </Card>
  );

  // 渲染分頁
  const renderPagination = () => {
    if (pagination.totalPages <= 1) return null;

    const items = [];
    const maxVisiblePages = isMobile ? 5 : 10;
    const halfVisible = Math.floor(maxVisiblePages / 2);
    
    let startPage = Math.max(1, pagination.currentPage - halfVisible);
    let endPage = Math.min(pagination.totalPages, startPage + maxVisiblePages - 1);
    
    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    // 第一頁
    if (startPage > 1) {
      items.push(
        <Pagination.Item key={1} onClick={() => handlePageChange(1)}>
          1
        </Pagination.Item>
      );
      if (startPage > 2) {
        items.push(<Pagination.Ellipsis key="ellipsis1" />);
      }
    }

    // 中間頁面
    for (let page = startPage; page <= endPage; page++) {
      items.push(
        <Pagination.Item
          key={page}
          active={page === pagination.currentPage}
          onClick={() => handlePageChange(page)}
        >
          {page}
        </Pagination.Item>
      );
    }

    // 最後一頁
    if (endPage < pagination.totalPages) {
      if (endPage < pagination.totalPages - 1) {
        items.push(<Pagination.Ellipsis key="ellipsis2" />);
      }
      items.push(
        <Pagination.Item 
          key={pagination.totalPages} 
          onClick={() => handlePageChange(pagination.totalPages)}
        >
          {pagination.totalPages}
        </Pagination.Item>
      );
    }

    return (
      <div className="d-flex justify-content-center mt-4">
        <Pagination>
          <Pagination.Prev 
            disabled={!pagination.hasPreviousPage}
            onClick={() => handlePageChange(pagination.currentPage - 1)}
          />
          {items}
          <Pagination.Next 
            disabled={!pagination.hasNextPage}
            onClick={() => handlePageChange(pagination.currentPage + 1)}
          />
        </Pagination>
      </div>
    );
  };

  // 渲染活動詳情模態框
  const renderDetailModal = () => (
    <Modal 
      show={showDetailModal} 
      onHide={() => setShowDetailModal(false)}
      size="lg"
      centered
    >
      <Modal.Header closeButton>
        <Modal.Title>{selectedActivity?.a_name}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {selectedActivity && (
          <Row>
            <Col md={6}>
              <h6>基本資訊</h6>
              <p><strong>主辦社別:</strong> {selectedActivity.store_name}</p>
              <p><strong>活動類別:</strong> {selectedActivity.activity_type_name || '無'}</p>
              <p><strong>活動日期:</strong> {selectedActivity.a_Sdate}</p>
              {selectedActivity.a_Edate && selectedActivity.a_Edate !== selectedActivity.a_Sdate && (
                <p><strong>結束日期:</strong> {selectedActivity.a_Edate}</p>
              )}
              <p><strong>活動地點:</strong> {selectedActivity.a_location ? (
                <a
                  href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(selectedActivity.a_location)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: '#17a2b8' }}
                >
                  {selectedActivity.a_location}
                </a>
              ) : '無'}</p>
              <p><strong>人數限制:</strong> {selectedActivity.a_people || '無限制'}</p>
              <p><strong>預算:</strong> {selectedActivity.a_budget ? `$${selectedActivity.a_budget}` : '無'}</p>
              <p><strong>負責人:</strong> {selectedActivity.organizer_name || '無'}</p>
            </Col>
            <Col md={6}>
              <h6>參與狀況</h6>
              <p><strong>已報名:</strong> <span className="text-success">{selectedActivity.registered_count}</span></p>
              <p><strong>已簽到:</strong> <span className="text-primary">{selectedActivity.checked_in_count}</span></p>
              <p><strong>請假:</strong> <span className="text-warning">{selectedActivity.leave_count}</span></p>
              
              <h6 className="mt-3">講師與主題</h6>
              <p>{selectedActivity.lecturer_topics || '無講師或主題資訊'}</p>
              
              <h6 className="mt-3">參訪公司</h6>
              <p>{selectedActivity.company_names || '無參訪公司'}</p>
              
              {selectedActivity.a_remark && (
                <>
                  <h6 className="mt-3">備註</h6>
                  <p>{selectedActivity.a_remark}</p>
                </>
              )}
            </Col>
          </Row>
        )}
      </Modal.Body>
    </Modal>
  );

  return (
    <Container fluid className="py-3">
      <Row>
        <Col>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h2>友社活動查詢</h2>
            <div className="d-flex gap-2 align-items-center">
              <Button
                variant="primary"
                size="sm"
                onClick={() => setShowReportModal(true)}
              >
                <FaFileAlt className="me-1" />
                社務報告
              </Button>
              <Button
                variant="info"
                size="sm"
                onClick={() => {
                  setShowLecturerModal(true);
                  loadYearlyLecturers();
                }}
              >
                <FaUserTie className="me-1" />
                年度講師
              </Button>
              <Button
                variant="success"
                size="sm"
                onClick={() => setShowAllActivitiesModal(true)}
              >
                <FaCalendarAlt className="me-1" />
                各社活動時間一覽表
              </Button>
              <Badge bg="info">
                共 {pagination.totalItems} 個活動
              </Badge>
            </div>
          </div>

          {error && (
            <Alert variant="danger" dismissible onClose={() => setError('')}>
              {error}
            </Alert>
          )}

          {renderFilters()}

          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
              <p className="mt-2">載入中...</p>
            </div>
          ) : activities.length === 0 ? (
            <Alert variant="info" className="text-center">
              <h5>未找到符合條件的活動</h5>
              <p>請調整篩選條件後重新搜尋</p>
            </Alert>
          ) : (
            <>
              {isMobile ? renderActivityCards() : renderActivityTable()}
              {renderPagination()}
            </>
          )}

          {renderDetailModal()}

          {isLightboxOpen && (
            <Lightbox
              mainSrc={lightboxImage}
              onCloseRequest={() => setIsLightboxOpen(false)}
            />
          )}

          {/* 社務報告 Modal */}
          <Modal
            show={showReportModal}
            onHide={() => {
              setShowReportModal(false);
              setReportData(null);
            }}
            size="lg"
            centered
          >
            <Modal.Header closeButton>
              <Modal.Title>
                <FaFileAlt className="me-2" />
                社務報告
              </Modal.Title>
            </Modal.Header>
            <Modal.Body>
              {!reportData ? (
                <Form>
                  <Row className="g-3">
                    <Col xs={12}>
                      <Form.Group>
                        <Form.Label>選擇社別 <span className="text-danger">*</span></Form.Label>
                        <Form.Select
                          value={reportFilters.storeId}
                          onChange={(e) => setReportFilters({ ...reportFilters, storeId: e.target.value })}
                        >
                          <option value="">請選擇社別</option>
                          {stores.map(store => (
                            <option key={store.ss_id} value={store.ss_id}>
                              {store.ss_name}
                            </option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                    </Col>
                    <Col xs={12} md={6}>
                      <Form.Group>
                        <Form.Label>開始日期 <span className="text-danger">*</span></Form.Label>
                        <Form.Control
                          type="date"
                          value={reportFilters.startDate}
                          onChange={(e) => setReportFilters({ ...reportFilters, startDate: e.target.value })}
                        />
                      </Form.Group>
                    </Col>
                    <Col xs={12} md={6}>
                      <Form.Group>
                        <Form.Label>結束日期 <span className="text-danger">*</span></Form.Label>
                        <Form.Control
                          type="date"
                          value={reportFilters.endDate}
                          onChange={(e) => setReportFilters({ ...reportFilters, endDate: e.target.value })}
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                </Form>
              ) : (
                <div>
                  <div className="mb-3 text-center">
                    <h5>{stores.find(s => s.ss_id === parseInt(reportFilters.storeId))?.ss_name} 社務報告</h5>
                    <small className="text-muted">
                      期間：{reportFilters.startDate} ~ {reportFilters.endDate}
                    </small>
                  </div>

                  <Accordion defaultActiveKey="0">
                    {categoryList.map((cat, idx) => {
                      const key = String(cat.id);  // 用 am_id 作為 key
                      const items = reportData[key] || [];
                      return items.length > 0 ? (
                        <Accordion.Item key={key} eventKey={idx.toString()}>
                          <Accordion.Header>
                            {cat.name} ({items.length} 場)
                          </Accordion.Header>
                          <Accordion.Body>
                            <Table size="sm" striped hover>
                              <thead>
                                <tr>
                                  <th style={{width: '30px'}}>#</th>
                                  <th style={{width: '120px'}}>活動日期</th>
                                  <th>活動名稱</th>
                                  {key === '1' && <th>講師與主題</th>}
                                  {key === '9' && <th>參訪公司</th>}
                                </tr>
                              </thead>
                              <tbody>
                                {items.map((item, i) => (
                                  <tr key={item.a_id}>
                                    <td>{i + 1}</td>
                                    <td><small>{item.a_Sdate}</small></td>
                                    <td>{item.a_name}</td>
                                    {key === '1' && (
                                      <td><small>{item.lecturer_topics || '-'}</small></td>
                                    )}
                                    {key === '9' && (
                                      <td><small>{item.company_names || '-'}</small></td>
                                    )}
                                  </tr>
                                ))}
                              </tbody>
                            </Table>
                          </Accordion.Body>
                        </Accordion.Item>
                      ) : null;
                    })}
                  </Accordion>

                  {Object.values(reportData).every(arr => arr.length === 0) && (
                    <Alert variant="info" className="text-center mt-3">
                      該期間內無活動記錄
                    </Alert>
                  )}
                </div>
              )}

              {loadingReport && (
                <div className="text-center py-3">
                  <Spinner animation="border" size="sm" />
                  <span className="ms-2">生成報告中...</span>
                </div>
              )}
            </Modal.Body>
            <Modal.Footer>
              <Button
                variant="secondary"
                onClick={() => {
                  setShowReportModal(false);
                  setReportData(null);
                }}
              >
                關閉
              </Button>
              {!reportData ? (
                <Button
                  variant="primary"
                  onClick={generateReport}
                  disabled={loadingReport || !reportFilters.storeId || !reportFilters.startDate || !reportFilters.endDate}
                >
                  生成報告
                </Button>
              ) : (
                <>
                  <Button
                    variant="outline-primary"
                    onClick={() => setReportData(null)}
                  >
                    重新選擇
                  </Button>
                  <Button
                    variant="success"
                    onClick={exportReportToExcel}
                  >
                    <FaFileExcel className="me-1" />
                    下載 Excel
                  </Button>
                  <Button
                    variant="info"
                    onClick={previewReportPDF}
                  >
                    <FaFilePdf className="me-1" />
                    預覽 PDF
                  </Button>
                </>
              )}
            </Modal.Footer>
          </Modal>

          {/* 年度講師 Modal */}
          <Modal
            show={showLecturerModal}
            onHide={() => {
              setShowLecturerModal(false);
              setLecturerData(null);
            }}
            size="lg"
            centered
          >
            <Modal.Header closeButton>
              <Modal.Title>
                <FaUserTie className="me-2" />
                年度講師統計
              </Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <Form.Group className="mb-3">
                <Form.Label>選擇年度</Form.Label>
                <div className="d-flex gap-2">
                  <Form.Control
                    type="number"
                    value={lecturerYear}
                    onChange={(e) => setLecturerYear(parseInt(e.target.value))}
                    min="2020"
                    max={new Date().getFullYear() + 1}
                  />
                  <Button
                    variant="primary"
                    onClick={loadYearlyLecturers}
                    disabled={loadingLecturer}
                  >
                    {loadingLecturer ? (
                      <>
                        <Spinner size="sm" className="me-1" />
                        載入中...
                      </>
                    ) : (
                      '查詢'
                    )}
                  </Button>
                </div>
              </Form.Group>

              {lecturerData && (
                <div>
                  <div className="mb-3 text-center">
                    <h5>{lecturerYear} 年度講師統計</h5>
                    <Badge bg="info">共 {lecturerData.length} 位講師</Badge>
                  </div>

                  {lecturerData.length > 0 ? (
                    <Table striped hover size="sm">
                      <thead className="table-dark">
                        <tr>
                          <th style={{width: '50px'}}>#</th>
                          <th>講師姓名</th>
                          <th style={{width: '80px'}}>演講次數</th>
                          <th>主要主題</th>
                          <th>主辦社別</th>
                        </tr>
                      </thead>
                      <tbody>
                        {lecturerData.map((lecturer, index) => (
                          <tr key={lecturer.l_id || index}>
                            <td>{index + 1}</td>
                            <td><strong>{lecturer.lecturer_name}</strong></td>
                            <td className="text-center">
                              <Badge bg="primary">{lecturer.topic_count}</Badge>
                            </td>
                            <td><small>{lecturer.topics || '-'}</small></td>
                            <td><small>{lecturer.stores || '-'}</small></td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  ) : (
                    <Alert variant="info" className="text-center">
                      該年度無講師記錄
                    </Alert>
                  )}
                </div>
              )}

              {loadingLecturer && !lecturerData && (
                <div className="text-center py-5">
                  <Spinner animation="border" />
                  <p className="mt-2">載入講師資料中...</p>
                </div>
              )}
            </Modal.Body>
            <Modal.Footer>
              <Button
                variant="secondary"
                onClick={() => {
                  setShowLecturerModal(false);
                  setLecturerData(null);
                }}
              >
                關閉
              </Button>
              {lecturerData && lecturerData.length > 0 && (
                <Button
                  variant="info"
                  onClick={previewLecturerPDF}
                >
                  <FaFilePdf className="me-1" />
                  預覽 PDF
                </Button>
              )}
            </Modal.Footer>
          </Modal>

          {/* 各社活動時間一覽表 Modal */}
          <Modal
            show={showAllActivitiesModal}
            onHide={() => {
              setShowAllActivitiesModal(false);
              setAllActivitiesData(null);
              setAllActivitiesFilters({ startDate: '', endDate: '' });
            }}
            size="xl"
            centered
          >
            <Modal.Header closeButton>
              <Modal.Title>
                <FaCalendarAlt className="me-2" />
                各社活動時間一覽表（全部）
              </Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <Form className="mb-3">
                <Row>
                  <Col md={5}>
                    <Form.Group>
                      <Form.Label>開始日期</Form.Label>
                      <Form.Control
                        type="date"
                        value={allActivitiesFilters.startDate}
                        onChange={(e) => setAllActivitiesFilters({
                          ...allActivitiesFilters,
                          startDate: e.target.value
                        })}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={5}>
                    <Form.Group>
                      <Form.Label>結束日期</Form.Label>
                      <Form.Control
                        type="date"
                        value={allActivitiesFilters.endDate}
                        onChange={(e) => setAllActivitiesFilters({
                          ...allActivitiesFilters,
                          endDate: e.target.value
                        })}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={2} className="d-flex align-items-end">
                    <Button
                      variant="primary"
                      onClick={loadAllActivities}
                      disabled={loadingAllActivities || !allActivitiesFilters.startDate || !allActivitiesFilters.endDate}
                      className="w-100"
                    >
                      {loadingAllActivities ? <Spinner animation="border" size="sm" /> : '查詢'}
                    </Button>
                  </Col>
                </Row>
              </Form>

              {allActivitiesData && (
                <div>
                  <div className="mb-3 text-center">
                    <h5>活動時間一覽表</h5>
                    <small className="text-muted">
                      期間：{allActivitiesFilters.startDate} ~ {allActivitiesFilters.endDate}
                    </small>
                    <br />
                    <Badge bg="success">共 {allActivitiesData.length} 個活動</Badge>
                  </div>

                  {allActivitiesData.length > 0 ? (
                    <div style={{ maxHeight: '500px', overflowY: 'auto' }}>
                      <Table striped hover size="sm">
                        <thead className="table-dark" style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                          <tr>
                            <th style={{width: '50px'}}>#</th>
                            <th style={{width: '100px'}}>活動日期</th>
                            <th style={{width: '120px'}}>類別</th>
                            <th style={{width: '100px'}}>社別</th>
                            <th>活動名稱</th>
                            <th style={{width: '200px'}}>地址</th>
                          </tr>
                        </thead>
                        <tbody>
                          {allActivitiesData.map((activity, index) => {
                            // 計算是否需要顯示日期（與上一筆不同才顯示）
                            const showDate = index === 0 ||
                              allActivitiesData[index - 1].a_Sdate !== activity.a_Sdate;

                            // 計算日期的 rowspan（連續相同日期的數量）
                            let dateRowSpan = 1;
                            if (showDate) {
                              for (let i = index + 1; i < allActivitiesData.length; i++) {
                                if (allActivitiesData[i].a_Sdate === activity.a_Sdate) {
                                  dateRowSpan++;
                                } else {
                                  break;
                                }
                              }
                            }

                            // 計算是否需要顯示類別（與上一筆的日期和類別都相同才不顯示）
                            const showCategory = index === 0 ||
                              allActivitiesData[index - 1].a_Sdate !== activity.a_Sdate ||
                              allActivitiesData[index - 1].activity_type_name !== activity.activity_type_name;

                            // 計算類別的 rowspan（在同一日期內，連續相同類別的數量）
                            let categoryRowSpan = 1;
                            if (showCategory) {
                              for (let i = index + 1; i < allActivitiesData.length; i++) {
                                if (allActivitiesData[i].a_Sdate === activity.a_Sdate &&
                                    allActivitiesData[i].activity_type_name === activity.activity_type_name) {
                                  categoryRowSpan++;
                                } else {
                                  break;
                                }
                              }
                            }

                            return (
                              <tr key={activity.a_id || index}>
                                <td>{index + 1}</td>
                                {showDate && (
                                  <td rowSpan={dateRowSpan} style={{ verticalAlign: 'middle' }}>
                                    {activity.a_Sdate}
                                  </td>
                                )}
                                {showCategory && (
                                  <td rowSpan={categoryRowSpan} style={{ verticalAlign: 'middle' }}>
                                    <Badge bg="secondary" className="text-wrap">
                                      {activity.activity_type_name || '-'}
                                    </Badge>
                                  </td>
                                )}
                                <td>
                                  <Badge bg="primary" className="text-wrap">
                                    {activity.store_name}
                                  </Badge>
                                </td>
                                <td><small>{activity.a_name}</small></td>
                                <td><small>{activity.a_location || '-'}</small></td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </Table>
                    </div>
                  ) : (
                    <Alert variant="info" className="text-center">
                      該期間無活動記錄
                    </Alert>
                  )}
                </div>
              )}

              {loadingAllActivities && !allActivitiesData && (
                <div className="text-center py-5">
                  <Spinner animation="border" />
                  <p className="mt-2">載入活動資料中...</p>
                </div>
              )}
            </Modal.Body>
            <Modal.Footer>
              <Button
                variant="secondary"
                onClick={() => {
                  setShowAllActivitiesModal(false);
                  setAllActivitiesData(null);
                  setAllActivitiesFilters({ startDate: '', endDate: '' });
                }}
              >
                關閉
              </Button>
              {allActivitiesData && allActivitiesData.length > 0 && (
                <Button
                  variant="success"
                  onClick={previewAllActivitiesPDF}
                >
                  <FaFilePdf className="me-1" />
                  預覽 PDF
                </Button>
              )}
            </Modal.Footer>
          </Modal>
        </Col>
      </Row>
    </Container>
  );
};

export default PartnerActivities;